const EMPTY_ALLOWED_PATHS = new Set<string>();
const EMPTY_SCOPED_ITEMS: readonly never[] = [];

export type NavigationPermissionStatus = 'pending' | 'ready' | 'error';

export interface NavigationPermissionState {
  userId: string | null;
  status: NavigationPermissionStatus;
  paths: ReadonlySet<string>;
}

export interface NavigationScopedItems<T> {
  scopeKey: string;
  items: T[];
}

export function getNavigationPermissionScopeKey(
  userId: string | null,
  allowedPaths: ReadonlySet<string>,
): string {
  return `${userId ?? 'anonymous'}\u0000${Array.from(allowedPaths).sort().join('\u0000')}`;
}

export function getVisibleNavigationScopedItems<T>(
  state: NavigationScopedItems<T>,
  currentScopeKey: string,
): readonly T[] {
  return state.scopeKey === currentScopeKey
    ? state.items
    : EMPTY_SCOPED_ITEMS as readonly T[];
}

export function canCommitNavigationUserLookup(
  expectedUserId: string | null,
  currentUserId: string | null,
  expectedGeneration: number,
  currentGeneration: number,
  signal: AbortSignal,
): boolean {
  return !signal.aborted
    && Boolean(expectedUserId)
    && expectedUserId === currentUserId
    && expectedGeneration === currentGeneration;
}

export function getEffectiveAllowedPaths(
  state: NavigationPermissionState,
  currentUserId: string | null,
): ReadonlySet<string> {
  if (!currentUserId || state.userId !== currentUserId || state.status !== 'ready') {
    return EMPTY_ALLOWED_PATHS;
  }
  return state.paths;
}

export function isPermissionEvaluationPending(
  state: NavigationPermissionState,
  currentUserId: string | null,
): boolean {
  return Boolean(currentUserId) && (state.userId !== currentUserId || state.status === 'pending');
}
